import { useState } from "react";
import { Link } from "react-router";
import { Plus } from "lucide-react";
import {
  CreateBase,
  EditBase,
  Form,
  InfiniteListBase,
  ListBase,
  ShowBase,
  email,
  required,
  useGetIdentity,
  useGetList,
  useListContext,
  useRecordContext,
} from "ra-core";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { TextInput } from "@/components/admin/text-input";
import { SelectInput } from "@/components/admin/select-input";
import { ReferenceInput } from "@/components/admin/reference-input";
import { ReferenceField } from "@/components/admin/reference-field";
import { useConfigurationContext } from "../root/ConfigurationContext";
import { FormToolbar } from "../layout/FormToolbar";
import { cleanupContactForCreate, cleanupContactForEdit } from "./contactModel";
import { ContactStatusSelector } from "./ContactInputs";
import { ContactTasksList } from "./ContactTasksList";
import { TaskCreateSheet } from "../tasks/TaskCreateSheet";
import { NoteCreate, NotesIterator } from "../notes";
import type { Contact, Sale, Task } from "../types";

const contactName = (contact: Contact) =>
  [contact.first_name, contact.last_name].filter(Boolean).join(" ");
const SaleName = () => {
  const sale = useRecordContext<Sale>();
  return (
    <span>
      {sale ? `${sale.first_name} ${sale.last_name}` : "Da assegnare"}
    </span>
  );
};

const Owner = () => (
  <ReferenceField source="sales_id" reference="sales" link={false}>
    <SaleName />
  </ReferenceField>
);

export const NarsClientList = () => (
  <ListBase
    resource="contacts"
    perPage={25}
    sort={{ field: "last_seen", order: "DESC" }}
    storeKey="nars.contacts"
    empty={false}
  >
    <ClientListContent />
  </ListBase>
);

const ClientListContent = () => {
  const {
    data,
    total,
    isPending,
    isFetching,
    error,
    refetch,
    filterValues,
    setFilters,
    page,
    setPage,
    perPage,
  } = useListContext<Contact>();
  const { noteStatuses } = useConfigurationContext();
  return (
    <div className="space-y-7">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Clienti</h1>
          <p className="mt-2 text-muted-foreground">
            Chi seguire, chi se ne occupa e cosa fare dopo.
          </p>
        </div>
        <Button asChild>
          <Link to="/contacts/create">
            <Plus className="size-4" /> Aggiungi cliente
          </Link>
        </Button>
      </div>
      <div className="flex flex-wrap gap-3">
        <Input
          aria-label="Cerca clienti"
          placeholder="Cerca un cliente…"
          className="max-w-sm"
          value={filterValues.q ?? ""}
          onChange={(event) =>
            setFilters(
              { ...filterValues, q: event.target.value },
              undefined,
              true,
            )
          }
        />
        <select
          aria-label="Filtra per interesse"
          className="h-9 rounded-md border border-input bg-background px-3 text-sm"
          value={filterValues.status ?? ""}
          onChange={(event) => {
            const next = { ...filterValues };
            if (event.target.value) next.status = event.target.value;
            else delete next.status;
            setFilters(next);
          }}
        >
          <option value="">Tutti gli interessi</option>
          {noteStatuses.map((status) => (
            <option key={status.value} value={status.value}>
              {status.label}
            </option>
          ))}
        </select>
      </div>
      {isPending || (isFetching && !data?.length) ? (
        <p role="status" className="py-12 text-muted-foreground">
          Caricamento clienti…
        </p>
      ) : error ? (
        <div role="alert">
          <p>Non riesco a caricare i clienti.</p>
          <Button variant="outline" onClick={() => refetch()}>
            Riprova
          </Button>
        </div>
      ) : !data?.length ? (
        <div className="border-y border-border py-16">
          <h2 className="text-xl font-medium">
            {filterValues.q || filterValues.status
              ? "Nessun cliente corrisponde alla ricerca."
              : "Il primo cliente parte da qui."}
          </h2>
          <p className="mt-3 max-w-md text-muted-foreground">
            {filterValues.q || filterValues.status
              ? "Prova a cambiare i filtri."
              : "Aggiungi un nome. Recapiti, note e prossimo ricontatto si possono completare anche dopo."}
          </p>
        </div>
      ) : (
        <div className="divide-y divide-border border-y border-border">
          {data.map((contact) => (
            <div
              key={contact.id}
              className="grid gap-3 py-5 sm:grid-cols-[1.3fr_0.8fr_1fr] sm:items-center"
            >
              <div>
                <Link
                  to={`/contacts/${encodeURIComponent(contact.id)}/show`}
                  className="font-medium hover:underline"
                >
                  {contactName(contact)}
                </Link>
                <p className="mt-1 text-sm text-muted-foreground">
                  {contact.company_name ||
                    contact.email_jsonb?.[0]?.email ||
                    "Recapiti da aggiungere"}
                </p>
              </div>
              <div className="text-sm">
                <span className="text-muted-foreground">
                  {noteStatuses.find(
                    (status) => status.value === contact.status,
                  )?.label ?? "Interesse da valutare"}
                </span>
                <div className="mt-1">
                  <span className="text-muted-foreground">Segue: </span>
                  <ReferenceField
                    record={contact}
                    source="sales_id"
                    reference="sales"
                    link={false}
                  >
                    <SaleName />
                  </ReferenceField>
                </div>
              </div>
              <NextAction contact={contact} />
            </div>
          ))}
        </div>
      )}
      {!!total && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>{total} clienti</span>
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
            >
              Indietro
            </Button>
            <span>Pagina {page}</span>
            <Button
              variant="ghost"
              disabled={page * perPage >= total}
              onClick={() => setPage(page + 1)}
            >
              Avanti
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

const NextAction = ({ contact }: { contact: Contact }) => {
  const { data, isPending, error } = useGetList<Task>("tasks", {
    pagination: { page: 1, perPage: 1 },
    sort: { field: "due_date", order: "ASC" },
    filter: { contact_id: contact.id, "done_date@is": null },
  });
  return (
    <div className="text-sm">
      <p className="text-xs text-muted-foreground">Prossimo passo</p>
      {isPending ? (
        "…"
      ) : error ? (
        "Da verificare"
      ) : data?.[0] ? (
        <>
          <p className="mt-1 font-medium">{data[0].text}</p>
          <p className="text-muted-foreground">
            {new Date(data[0].due_date).toLocaleDateString("it-IT")}
          </p>
        </>
      ) : (
        <Link
          to={`/contacts/${encodeURIComponent(contact.id)}/show`}
          className="mt-1 inline-block underline underline-offset-4"
        >
          Programma un ricontatto
        </Link>
      )}
    </div>
  );
};

const ClientInputs = () => {
  const { noteStatuses } = useConfigurationContext();
  return (
    <div className="space-y-5">
      <TextInput
        source="first_name"
        label="Nome cliente o brand"
        validate={required()}
        helperText={false}
      />
      <details className="group">
        <summary className="mb-4 cursor-pointer text-sm text-muted-foreground">
          Aggiungi recapiti e dettagli
        </summary>
        <div className="grid gap-4 sm:grid-cols-2">
          <TextInput
            source="last_name"
            label="Cognome / referente"
            helperText={false}
          />
          <TextInput
            source="email_jsonb.0.email"
            label="Email"
            validate={email()}
            helperText={false}
          />
          <TextInput
            source="phone_jsonb.0.number"
            label="Telefono"
            helperText={false}
          />
          <TextInput
            source="title"
            label="Ruolo del referente"
            helperText={false}
          />
        </div>
      </details>
      <div className="grid gap-4 sm:grid-cols-2">
        <SelectInput
          source="status"
          label="Interesse"
          choices={noteStatuses}
          optionText="label"
          optionValue="value"
          translateChoice={false}
          helperText={false}
        />
        <ReferenceInput
          source="sales_id"
          reference="sales"
          filter={{ "disabled@neq": true }}
        >
          <SelectInput
            label="Responsabile"
            optionText={(sale: Sale) => `${sale.first_name} ${sale.last_name}`}
            validate={required()}
            helperText={false}
          />
        </ReferenceInput>
      </div>
      <TextInput
        source="background"
        label="Informazioni utili"
        multiline
        helperText="Ad esempio: sito, origine del contatto, bisogno e servizio da proporre."
      />
    </div>
  );
};

const cleanValues = (data: Contact) => ({
  ...data,
  email_jsonb: (data.email_jsonb ?? [])
    .filter((item) => item.email?.trim())
    .map((item) => ({ ...item, type: item.type || "Work" })),
  phone_jsonb: (data.phone_jsonb ?? [])
    .filter((item) => item.number?.trim())
    .map((item) => ({ ...item, type: item.type || "Work" })),
});

export const NarsClientCreate = () => {
  const { identity } = useGetIdentity();
  if (!identity) return <p role="status">Caricamento…</p>;
  return (
    <CreateBase
      redirect="show"
      transform={(data: Contact) => cleanupContactForCreate(cleanValues(data))}
    >
      <div className="mx-auto max-w-2xl space-y-7">
        <Link to="/contacts" className="text-sm text-muted-foreground">
          ← Clienti
        </Link>
        <h1 className="text-3xl font-semibold tracking-tight">
          Aggiungi cliente
        </h1>
        <Form
          defaultValues={{
            last_name: "",
            sales_id: identity.id,
            status: "cold",
            email_jsonb: [{ email: "", type: "Work" }],
            phone_jsonb: [{ number: "", type: "Work" }],
          }}
        >
          <ClientInputs />
          <FormToolbar />
        </Form>
      </div>
    </CreateBase>
  );
};

export const NarsClientEdit = () => (
  <EditBase
    redirect="show"
    mutationMode="pessimistic"
    transform={(data: Contact) => cleanupContactForEdit(cleanValues(data))}
  >
    <div className="mx-auto max-w-2xl space-y-7">
      <h1 className="text-3xl font-semibold tracking-tight">
        Modifica cliente
      </h1>
      <Form>
        <ClientInputs />
        <FormToolbar />
      </Form>
    </div>
  </EditBase>
);

export const NarsClientShow = () => (
  <ShowBase>
    <ClientShowContent />
  </ShowBase>
);

const ClientShowContent = () => {
  const contact = useRecordContext<Contact>();
  const [taskOpen, setTaskOpen] = useState(false);
  if (!contact) return <p role="status">Caricamento cliente…</p>;
  return (
    <div className="space-y-8">
      <Link to="/contacts" className="text-sm text-muted-foreground">
        ← Clienti
      </Link>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">
            {contactName(contact)}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Responsabile: <Owner />
          </p>
        </div>
        <Button asChild variant="outline">
          <Link to={`/contacts/${encodeURIComponent(contact.id)}`}>
            Modifica
          </Link>
        </Button>
      </div>
      <div className="flex flex-wrap items-center gap-4">
        <ContactStatusSelector />
        {contact.email_jsonb?.[0]?.email && (
          <a
            className="text-sm underline"
            href={`mailto:${contact.email_jsonb[0].email}`}
          >
            {contact.email_jsonb[0].email}
          </a>
        )}
        {contact.phone_jsonb?.[0]?.number && (
          <span className="text-sm">{contact.phone_jsonb[0].number}</span>
        )}
      </div>
      {contact.background && (
        <p className="max-w-2xl whitespace-pre-wrap text-sm text-muted-foreground">
          {contact.background}
        </p>
      )}
      <div className="grid gap-10 lg:grid-cols-[1.3fr_1fr]">
        <section className="min-w-0 border-t border-border pt-5">
          <h2 className="text-lg font-medium">Contatti e aggiornamenti</h2>
          <p className="mb-4 mt-1 text-sm text-muted-foreground">
            Annota cosa hai inviato, il canale e la risposta. Autore e data
            restano nello storico.
          </p>
          <InfiniteListBase
            resource="contact_notes"
            filter={{ contact_id: contact.id }}
            sort={{ field: "date", order: "DESC" }}
            perPage={25}
            disableSyncWithLocation
            storeKey={false}
            empty={
              <NoteCreate reference="contacts" showStatus className="mt-4" />
            }
          >
            <NotesIterator reference="contacts" showStatus />
          </InfiniteListBase>
        </section>
        <section className="min-w-0 border-t border-border pt-5">
          <div className="mb-5 flex items-center justify-between gap-2">
            <h2 className="text-lg font-medium">Prossimi passi</h2>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setTaskOpen(true)}
            >
              <Plus className="size-4" /> Ricontatto
            </Button>
          </div>
          <ContactTasksList />
        </section>
      </div>
      <TaskCreateSheet
        open={taskOpen}
        onOpenChange={setTaskOpen}
        contact_id={contact.id}
      />
    </div>
  );
};
